import React, { useContext, useEffect, useState } from "react"
import { TdsTextarea, TdsTextField } from "@scania/tegel-react"
import "./GenericFields.scss"
import { scopeValues, variableTypesList } from "../../../helpers/genericDropdownList"
import { UserActionContext } from "../../../AppContext"
import { useFormContext } from "react-hook-form"
import { getDisplayValueForTextFields } from "../../../helpers/getDisplayValue"
import { isReadOnly } from "../../../helpers/isReadOnly"
import { isNotUpdateable } from "../../../helpers/isNotUpdateable"
import { useMobXStore } from "../../../hooks/useMobXStore"
import { observer } from "mobx-react"
import { newVariable } from '../../../helpers/dataHelper';
import GenericDropdown from "../../GenericDropdown/GenericDropdown"
import TextField from "../../TextField/TextField"
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog"

type GenericFieldsProps = {
    selectedVariableDetails?: VariableDetail
    isEditor: boolean
}


const GenericFields = ({ isEditor, selectedVariableDetails }: GenericFieldsProps) => {
    const { action, updateWithVersion } = useContext(UserActionContext);
    const { register, setValue, getValues } = useFormContext();
    const { variableDetailsStore } = useMobXStore()
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    const onConfirmTypeChange = (variableType:string) => {
        if(action === 'create'){
            setValue("scope",scopeValues[variableType as VariableType])
        }else{
            setShowConfirmationDialog(true);
        }
    };

    const handleConfirmationTypeChange = (confirmed: boolean) => {
        if (confirmed) {
            const selectedType = getValues("type") as VariableType; // Always 'string' in this context
            const variableData = (action === 'create') ? { ...newVariable } : { ...selectedVariableDetails };

            if (variableData) {
                variableData.type = selectedType;
                variableData.scope = scopeValues[selectedType];
                variableDetailsStore.setSelectedVariableDetails(variableData as VariableDetail);

                // Update field values based on the new variable data
                setValue('type', variableData.type);
                setValue('scope', variableData.scope);
                // ... update other fields as needed
            }
        } else {
            // Reset the type to the previous value if not confirmed
            resetType();
        }

        setShowConfirmationDialog(false);
    };

    const resetType = () => {
        setValue('type', selectedVariableDetails?.type);
    };

    useEffect(() => {
        setValue('type', selectedVariableDetails?.type);
        setValue('scope', scopeValues[selectedVariableDetails?.type || 'gauge']);
        if (action !== 'create') setValue('id', selectedVariableDetails?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariableDetails?.type, action]);



    return <>
        <div className='tds-row'>
            <div className="tds-col-sm-6">
                {(isReadOnly(isEditor, action) || isNotUpdateable(action, 'Variable type', updateWithVersion)) ?
                    <TdsTextField
                        type="text"
                        size="md"
                        state="default"
                        modeVariant="secondary"
                        label="Variable type *"
                        labelPosition="outside"
                        readOnly={true}
                        value={getDisplayValueForTextFields(selectedVariableDetails?.type, action)}

                    >
                    </TdsTextField>
                    :
                    <GenericDropdown
                        className="variable-type-dropdown"
                        label="Variable type *"
                        labelPosition="outside"
                        placeholder="Select type"
                        size="md"
                        modeVariant="secondary"
                        openDirection="down"
                        onDropdownChange={onConfirmTypeChange}
                        listData={variableTypesList}
                        dataFieldName="type"
                        defaultValue={getValues("type")}
                    />
                }

            </div>
            <div className="tds-col-sm-6">
                <TextField
                    type="text"
                    size="md"
                    state="default"
                    modeVariant="secondary"
                    placeholder="Enter the minimum CDC version required"
                    label="Minimum CDC version"
                    labelPosition="outside"
                    readOnly={isReadOnly(isEditor, action) || isNotUpdateable(action, 'Minimum CDC version', updateWithVersion)}
                    dataFieldName="minimum_cdc_version"
                />
            </div>
        </div>
        <div className="tds-row tds-u-mt3">
            <div className="tds-col-sm-12">
                <TextField
                    type="text"
                    size="md"
                    modeVariant="secondary"
                    placeholder="Enter a name "
                    label="Variable name *"
                    labelPosition="outside"
                    readOnly={isReadOnly(isEditor, action) || isNotUpdateable(action, 'Variable name', updateWithVersion)}
                    dataFieldName="name"
                    required="This field is required!"
                />
            </div>
        </div>
        <div className="tds-row tds-u-mt3">
            <div className="tds-col-sm-6">
                <TextField
                    type="text"
                    size="md"
                    modeVariant="secondary"
                    placeholder="Enter current status name for the variable"
                    label="Current status name"
                    labelPosition="outside"
                    readOnly={isReadOnly(isEditor, action) || isNotUpdateable(action, 'Current status name', updateWithVersion)}
                    dataFieldName="current_status_name"
                />
            </div>
            <div className="tds-col-sm-6">
                <TextField
                    type="text"
                    size="md"
                    modeVariant="secondary"
                    placeholder="Enter RFMS name for the variable"
                    label="RFMS name"
                    labelPosition="outside"
                    readOnly={isReadOnly(isEditor, action) || isNotUpdateable(action, 'RFMS name', updateWithVersion)}
                    dataFieldName="rfms_name" />
            </div>
        </div>
        <div className="tds-row tds-u-mt3">
            <div className="tds-col-sm-12">
                <TdsTextarea
                    state="default"
                    rows={2}
                    modeVariant="secondary"
                    placeholder="Enter a description for the variable you are creating"
                    label="Description"
                    labelPosition="outside"
                    readOnly={isReadOnly(isEditor, action) || isNotUpdateable(action, 'Description', updateWithVersion)}
                    value={getValues("description")}
                    {...register("description")}
                >
                </TdsTextarea>
            </div>
        </div>

        {showConfirmationDialog && (
            <ConfirmationDialog
                onCancel={() => setShowConfirmationDialog(false)}
                onConfirm={handleConfirmationTypeChange}
                onReset={resetType} // Add a reset function
            />
        )}
    </>
}

export default observer(GenericFields)